import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { UserCircle, Crown, Warning, ArrowsClockwise } from "@phosphor-icons/react";

// Use UserCircle as the driver icon (Steering not available in phosphor-icons)
const Steering = UserCircle;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
  critical: "text-red-400",
  high: "text-orange-400",
  medium: "text-amber-400",
  low: "text-emerald-400",
};

export default function DriverPerformancePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ai/drivers/leaderboard?limit=4`);
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="driver-performance-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 flex items-center gap-1.5">
          <Steering size={12} weight="duotone" className="text-teal-400" />
          Driver Performance
        </h3>
        <button onClick={fetch} className="text-zinc-500 hover:text-teal-400">
          <ArrowsClockwise size={11} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading ? (
        <div className="text-xs font-mono text-zinc-500 animate-pulse text-center py-4">Calculating...</div>
      ) : !data || data.total_drivers === 0 ? (
        <div className="text-center py-4">
          <Steering size={24} weight="duotone" className="text-zinc-700 mx-auto mb-2" />
          <p className="text-xs text-zinc-500">No driver data yet</p>
          <p className="text-[10px] text-zinc-600 mt-1">Assign drivers to shipments to see performance</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Top performers */}
          {data.top_performers?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1">
                <Crown size={9} className="text-emerald-400" /> Top Performers
              </p>
              <div className="space-y-1" data-testid="top-performers">
                {data.top_performers.slice(0, 3).map(d => (
                  <DriverRow key={d.driver_name} driver={d} positive />
                ))}
              </div>
            </div>
          )}
          {/* Top risk */}
          {data.top_risk?.filter(d => d.risk_score > 0).length > 0 && (
            <div className="pt-3 border-t border-zinc-800">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1">
                <Warning size={9} className="text-amber-400" /> At Risk
              </p>
              <div className="space-y-1" data-testid="at-risk-drivers">
                {data.top_risk.filter(d => d.risk_score > 0).slice(0, 3).map(d => (
                  <DriverRow key={d.driver_name} driver={d} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function DriverRow({ driver, positive }) {
  const color = LEVEL_COLORS[driver.risk_level] || "text-zinc-400";
  return (
    <div className="flex items-center gap-2 p-1.5 rounded-sm bg-zinc-950/50"
      data-testid={`driver-row-${driver.driver_name}`}>
      <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center shrink-0">
        <span className="text-[9px] font-mono font-bold text-zinc-400">
          {driver.driver_name.split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-mono text-zinc-200 truncate">{driver.driver_name}</p>
        <p className="text-[9px] font-mono text-zinc-500">{driver.shipments} trips</p>
      </div>
      {positive && driver.avg_compliance != null && (
        <span className="text-[10px] font-mono font-bold text-emerald-400">{driver.avg_compliance}%</span>
      )}
      {!positive && (
        <span className={`text-[10px] font-mono font-bold uppercase tracking-wider ${color}`}>
          {driver.risk_level}
        </span>
      )}
    </div>
  );
}
